import { useEffect, useState } from "react";

import type { RoleOut } from "@/api/queries";
import { Button } from "@/components/ui/button";
import { FieldLabel, Input } from "@/components/ui/input";
import { ErrorNote } from "@/components/ui/panel";

const ROLE_KEY_RE = /^[a-z0-9_-]{2,32}$/;

export function CreateRoleModal({
  open,
  roles,
  isPending,
  error,
  onClose,
  onCreate,
}: {
  open: boolean;
  roles: RoleOut[];
  isPending?: boolean | undefined;
  error?: unknown;
  onClose: () => void;
  onCreate: (data: {
    role: string;
    title: string;
    purpose: string;
    cloneFrom?: string | undefined;
  }) => void;
}) {
  const [title, setTitle] = useState("");
  const [roleKey, setRoleKey] = useState("");
  const [purpose, setPurpose] = useState("");
  const [cloneFrom, setCloneFrom] = useState<string>("viewer");
  const [keyEdited, setKeyEdited] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setRoleKey("");
      setPurpose("");
      setCloneFrom("viewer");
      setKeyEdited(false);
      setValidationError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isPending) onClose();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [open, isPending, onClose]);

  if (!open) return null;

  function handleTitleChange(val: string) {
    setTitle(val);
    if (!keyEdited) {
      const slug = val
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "_")
        .slice(0, 32);
      setRoleKey(slug);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const slug = roleKey.trim().toLowerCase();
    if (!title.trim()) {
      setValidationError("Role name is required");
      return;
    }
    if (!ROLE_KEY_RE.test(slug)) {
      setValidationError("Role key must be 2-32 lowercase characters, digits, '_' or '-'");
      return;
    }
    if (roles.some((r) => r.role.toLowerCase() === slug)) {
      setValidationError(`Role key "${slug}" already exists`);
      return;
    }
    setValidationError(null);
    onCreate({
      role: slug,
      title: title.trim(),
      purpose: purpose.trim(),
      cloneFrom: cloneFrom || undefined,
    });
  }

  return (
    <div className="bg-bank-ink/40 fixed inset-0 z-50 grid place-items-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Create New Role"
        className="border-bank-border bg-bank-surface w-full max-w-lg overflow-hidden rounded-xl border shadow-2xl"
      >
        <form onSubmit={handleSubmit}>
          <div className="border-bank-border flex items-center justify-between border-b px-5 py-3.5">
            <h2 className="text-base font-semibold tracking-tight">Create New Role</h2>
            <button
              type="button"
              disabled={isPending}
              onClick={onClose}
              className="text-bank-muted hover:text-bank-ink rounded p-1 text-sm transition-colors"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4 p-5">
            <div>
              <FieldLabel htmlFor="new-role-name">Role Name</FieldLabel>
              <Input
                id="new-role-name"
                value={title}
                autoFocus
                placeholder="e.g. Quality Auditor, Content Reviewer"
                onChange={(e) => handleTitleChange(e.target.value)}
              />
            </div>

            <div>
              <FieldLabel htmlFor="new-role-key">Role Key (Identifier)</FieldLabel>
              <Input
                id="new-role-key"
                value={roleKey}
                placeholder="e.g. quality_auditor"
                onChange={(e) => {
                  setKeyEdited(true);
                  setRoleKey(e.target.value.toLowerCase());
                }}
              />
              <p className="text-bank-muted mt-1 text-[11px]">
                Unique system slug. Letters, numbers, hyphens, and underscores only.
              </p>
            </div>

            <div>
              <FieldLabel htmlFor="new-role-purpose">Purpose / Description</FieldLabel>
              <Input
                id="new-role-purpose"
                value={purpose}
                placeholder="What responsibilities this role holds in the platform"
                onChange={(e) => setPurpose(e.target.value)}
              />
            </div>

            <div>
              <FieldLabel htmlFor="new-role-clone">Clone Permissions From</FieldLabel>
              <select
                id="new-role-clone"
                value={cloneFrom}
                onChange={(e) => setCloneFrom(e.target.value)}
                className="border-bank-border-strong bg-bank-surface text-bank-ink focus-visible:ring-bank-accent h-9 w-full rounded-md border px-3 text-sm focus-visible:ring-1 focus-visible:outline-none"
              >
                <option value="">Start Empty</option>
                {roles.map((r) => (
                  <option key={r.role} value={r.role}>
                    {r.title || r.role} ({r.permissions.length} permissions)
                  </option>
                ))}
              </select>
            </div>

            {validationError && (
              <p className="bg-bank-bad-wash text-bank-bad-ink rounded-md p-2.5 text-xs font-medium">
                {validationError}
              </p>
            )}

            <ErrorNote error={error} />
          </div>

          <div className="border-bank-border bg-bank-surface-2 flex items-center justify-end gap-2 border-t px-5 py-3">
            <Button type="button" variant="ghost" disabled={isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? "Creating…" : "Create Role"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
